import { ref } from 'vue';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const DEFAULT_LAT = 41.0082; // Istanbul
const DEFAULT_LNG = 28.9784;

/**
 * Composable for managing Mapbox map and marker
 */
export function useMapboxMap(mapboxToken, canEdit, initialLocation = null) {
  const map = ref(null);
  const marker = ref(null);
  const isPinDragged = ref(false);
  
  const currentLocation = ref(initialLocation || {
    lat: DEFAULT_LAT,
    lng: DEFAULT_LNG
  });

  const initMap = (containerId = 'map-container', onDragEnd = null) => {
    if (!mapboxToken) {
      console.error('Mapbox access token is required');
      return;
    }

    const initialLat = currentLocation.value.lat;
    const initialLng = currentLocation.value.lng;

    // Create map
    map.value = new mapboxgl.Map({
      container: containerId,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [initialLng, initialLat],
      zoom: 13
    });

    // Add navigation controls
    map.value.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Create custom marker element
    const el = document.createElement('div');
    el.className = 'custom-marker';
    el.innerHTML = '<i class="ti ti-map-pin" style="font-size: 32px; color: #00A0DF;"></i>';
    el.style.cursor = canEdit.value ? 'grab' : 'default';
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'center';

    // Add marker for current location
    marker.value = new mapboxgl.Marker({
      element: el,
      draggable: canEdit.value
    })
      .setLngLat([initialLng, initialLat])
      .addTo(map.value);

    // Handle marker drag events
    marker.value.on('dragstart', () => {
      isPinDragged.value = false;
      el.style.cursor = 'grabbing';
    });

    marker.value.on('dragend', () => {
      const position = marker.value.getLngLat();
      currentLocation.value = {
        lat: position.lat,
        lng: position.lng
      };
      
      isPinDragged.value = true;
      el.style.cursor = canEdit.value ? 'grab' : 'default';
      
      if (onDragEnd) {
        onDragEnd(position.lat, position.lng);
      }
      
      setTimeout(() => {
        isPinDragged.value = false;
      }, 2000);
    });

    // Disable automatic keyboard focus on map
    map.value.getContainer().setAttribute('tabindex', '-1');
    map.value.on('load', () => {
      map.value.getContainer().style.outline = 'none';
    });
  };

  const updateMapLocation = (lat, lng, zoom = 15) => {
    if (map.value && marker.value) {
      map.value.flyTo({ center: [lng, lat], zoom });
      marker.value.setLngLat([lng, lat]);
    }
    // Sync internal location (used for initial positioning)
    currentLocation.value = { lat, lng };
  };

  const cleanup = () => {
    if (map.value) {
      map.value.remove();
      map.value = null;
    }
    if (marker.value) {
      marker.value = null;
    }
  };

  return {
    map,
    marker,
    currentLocation,
    isPinDragged,
    initMap,
    updateMapLocation,
    cleanup
  };
}

